<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { useAppStore } from '@/store/modules/app'
import { enterGateMode, enterGateTick } from './enterGate'

const appStore = useAppStore()
const show = ref(false)
const leaving = ref(false)
let timers: number[] = []
let startedAt = 0

const clearTimers = () => {
  timers.forEach((timer) => window.clearTimeout(timer))
  timers = []
}

const later = (fn: () => void, delay: number) => {
  timers.push(window.setTimeout(fn, delay))
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

const hideNow = () => {
  clearTimers()
  show.value = false
  leaving.value = false
}

const playOut = () => {
  if (!show.value || leaving.value) return
  const wait = Math.max(0, 1500 - (Date.now() - startedAt))
  later(() => {
    leaving.value = true
    later(hideNow, 760)
  }, wait)
}

watch(enterGateTick, () => {
  if (enterGateMode.value === 'start') {
    if (reducedMotion()) return
    clearTimers()
    startedAt = Date.now()
    leaving.value = false
    show.value = true
    return
  }
  if (enterGateMode.value === 'cancel') {
    hideNow()
    return
  }
  playOut()
})

onBeforeUnmount(clearTimers)
</script>

<template>
  <div v-if="show" class="enter-gate" :class="{ 'is-leaving': leaving }">
    <div class="enter-gate__grid"></div>
    <div class="enter-gate__beam"></div>
    <div class="enter-gate__core">
      <div class="enter-gate__mark">
        <div class="enter-gate__halo"></div>
        <svg class="enter-gate__ring" viewBox="0 0 120 120" aria-hidden="true">
          <circle cx="60" cy="60" r="54" />
        </svg>
        <img class="enter-gate__logo" src="@/assets/imgs/jida.png" alt="" />
      </div>
      <h2 class="enter-gate__title">{{ appStore.getTitle }}</h2>
      <p class="enter-gate__status"><span>正在进入平台</span></p>
    </div>
  </div>
</template>

<style scoped>
.enter-gate {
  position: fixed;
  inset: 0;
  z-index: 10000;
  overflow: hidden;
  pointer-events: auto;
  background:
    radial-gradient(circle at 50% 42%, rgba(0, 225, 235, 0.16), transparent 36%),
    #061428 url('@/assets/imgs/login_bg.png') center / cover no-repeat;
}

.enter-gate.is-leaving {
  animation: enter-open 0.72s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.enter-gate__grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 225, 235, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 225, 235, 0.08) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(circle at center, #000 0%, transparent 68%);
  animation: enter-grid 1.6s ease-out both;
  pointer-events: none;
}

.enter-gate__beam {
  position: absolute;
  left: 0;
  width: 100%;
  height: 18%;
  background: linear-gradient(180deg, transparent, rgba(0, 225, 235, 0.18), transparent);
  animation: enter-beam 1.35s ease-in-out 0.15s both;
  pointer-events: none;
}

.enter-gate__core {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  display: flex;
  width: min(92vw, 420px);
  transform: translate(-50%, -50%);
  flex-direction: column;
  align-items: center;
  animation: enter-core 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.enter-gate.is-leaving .enter-gate__core,
.enter-gate.is-leaving .enter-gate__grid,
.enter-gate.is-leaving .enter-gate__beam {
  opacity: 0;
  transition: opacity 0.28s ease;
}

.enter-gate__mark {
  position: relative;
  display: flex;
  width: 168px;
  height: 168px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}

.enter-gate__halo {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, rgba(0, 225, 235, 0.45), transparent 68%);
  filter: blur(8px);
  animation: enter-halo 1.4s ease-in-out infinite;
}

.enter-gate__ring {
  position: absolute;
  inset: 8px;
  width: auto;
  height: auto;
  transform: rotate(-90deg);
}

.enter-gate__ring circle {
  fill: none;
  stroke: #00e1eb;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 340;
  stroke-dashoffset: 340;
  filter: drop-shadow(0 0 8px rgba(0, 225, 235, 0.8));
  animation: enter-ring 1.1s ease-out 0.1s forwards;
}

.enter-gate__logo {
  position: relative;
  width: 92px;
  height: 92px;
  object-fit: contain;
  filter: drop-shadow(0 0 18px rgba(0, 225, 235, 0.45));
}

.enter-gate__title {
  position: relative;
  z-index: 1;
  margin: 28px 0 0;
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
  text-align: center;
  line-height: 1.3;
}

.enter-gate__status {
  margin: 12px 0 0;
  color: #7ef6fb;
  font-size: 14px;
  letter-spacing: 4px;
}

.enter-gate__status span {
  display: inline-block;
  animation: enter-status 1.2s ease-in-out infinite;
}

@keyframes enter-status {
  50% {
    opacity: 0.35;
  }
}

@keyframes enter-ring {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes enter-core {
  from {
    opacity: 0;
    transform: translate(-50%, -46%) scale(0.86);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes enter-halo {
  50% {
    opacity: 0.55;
    transform: scale(1.08);
  }
}

@keyframes enter-beam {
  from {
    top: -20%;
  }
  to {
    top: 110%;
  }
}

@keyframes enter-open {
  to {
    opacity: 0;
    transform: scale(1.06);
    filter: brightness(1.6);
  }
}

@keyframes enter-grid {
  from {
    opacity: 0;
    transform: scale(1.04);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .enter-gate__mark {
    width: 140px;
    height: 140px;
  }

  .enter-gate__title {
    font-size: 22px;
  }

  .enter-gate__logo {
    width: 76px;
    height: 76px;
  }
}
</style>
